import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Eye,
  TrendingUp,
  Gauge,
  Scale,
  ArrowRight,
  Check,
  Sparkles,
  X,
  Loader2,
} from "lucide-react";
import dioroLogoAsset from "@/assets/dioro-logo-lockup.png.asset.json";
import dioroSeesawAsset from "@/assets/dioro-seesaw-icon.png.asset.json";

const dioroTitle = "Dioro by SaaSed — See through your Salesforce licenses";
const dioroDescription =
  "Dioro, SaaSed's license audit tool, reveals Salesforce usage insight, forecasting, optimisation and benchmarking — so every seat you pay for earns its keep.";

const dioroSoftwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": "https://saased.co/dioro#software",
  name: "Dioro",
  applicationCategory: "BusinessApplication",
  url: "https://saased.co/dioro",
  description: dioroDescription,
  provider: { "@id": "https://saased.co/#organization" },
};

export const Route = createFileRoute("/dioro")({
  head: () => ({
    meta: [
      { title: dioroTitle },
      {
        name: "description",
        content: dioroDescription,
      },
      { property: "og:title", content: dioroTitle },
      { property: "og:description", content: dioroDescription },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: dioroTitle },
      { name: "twitter:description", content: dioroDescription },
      { property: "og:url", content: "https://saased.co/dioro" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/dioro" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(dioroSoftwareSchema) },
    ],
  }),
  component: DioroLanding,
});

const INK = "#1a1408";
const CREAM = "#fffdf0";
const LIME = "#9dc800";
const SAND = "#f0e8c8";
const MUTED = "#6b5f42";
const DISPLAY = "'Bricolage Grotesque', serif";
const BODY = "'DM Sans', sans-serif";
const CARD_BORDER = { border: `2px solid ${INK}` };
const CARD_SHADOW = { boxShadow: `6px 6px 0 0 ${INK}` };
const HERO_WASH = `radial-gradient(ellipse 58% 52% at 74% 18%, #e3ecb4 0%, ${CREAM} 50%, ${CREAM} 100%)`;
const HERO_WASH_GREEN = `linear-gradient(135deg, ${CREAM} 0%, #eef3b8 55%, #d7e585 100%)`;

function DioroMark({ className = "h-9 w-9" }: { className?: string }) {
  return (
    <svg viewBox="0 0 42 42" fill="none" className={className} aria-hidden="true">
      <path d="M4 28 l34 -9" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      <path d="M21 23.5 v8.5" stroke={INK} strokeWidth="2" strokeLinecap="round" />
      <circle cx="4" cy="28" r="3.4" fill={INK} />
      <circle cx="38" cy="19" r="3.4" fill={LIME} />
    </svg>
  );
}

const useCases = [
  {
    icon: Eye,
    kicker: "01",
    title: "Insight into usage",
    body: "See exactly who logs in, what they touch and which features go untouched — per license type, team and region.",
  },
  {
    icon: Gauge,
    kicker: "02",
    title: "License optimisation",
    body: "Refine license mix, reclaim dormant seats, model swap scenarios before negotiation",
  },
  {
    icon: TrendingUp,
    kicker: "03",
    title: "Maximise your Salesforce budget",
    body: "Understand and uncover blind spots, allocate your software spend effectively",
  },
  {
    icon: Scale,
    kicker: "04",
    title: "Manage compliance requirements",
    body: "Audit license usage to ensure every seat, entitlement, and permission aligns with regulatory and contractual obligations.",
  },
];

const proofPoints = [
  { value: "18.3%", label: "average dormant seats found in a first audit" },
  { value: "4 wks", label: "from connection to a renewal-ready business case" },
  { value: "100%", label: "read-only — nothing changes in your org" },
];

const HONEYPOT_NAME = "b_b0b56a1a3a62f7fdadb7fe265_51a44aab1c";
const MC_JSONP_BASE =
  "https://saased.us22.list-manage.com/subscribe/post-json?u=b0b56a1a3a62f7fdadb7fe265&id=51a44aab1c&f_id=0051c2e1f0";

const BLOCKED_EMAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "hotmail.com", "hotmail.co.uk",
  "outlook.com", "outlook.co.uk", "live.com", "live.co.uk",
  "msn.com", "yahoo.com", "yahoo.co.uk", "ymail.com", "rocketmail.com",
  "aol.com", "icloud.com", "me.com", "mac.com", "protonmail.com",
  "proton.me", "gmx.com", "gmx.net", "mail.com", "zoho.com",
  "yandex.com", "yandex.ru", "inbox.com", "hushmail.com",
]);

function isBlockedEmailDomain(email: string): boolean {
  const at = email.lastIndexOf("@");
  if (at === -1) return false;
  const domain = email.slice(at + 1).trim().toLowerCase();
  return BLOCKED_EMAIL_DOMAINS.has(domain);
}

function DioroLanding() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const [waitlistSending, setWaitlistSending] = useState(false);

  // --- Contact form (CTA section) ---
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  async function submitContact(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setContactError(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    if (isBlockedEmailDomain(email)) {
      setEmailError("Please use your work email address");
      return;
    }
    setEmailError(null);
    setContactSubmitting(true);
    try {
      const res = await fetch("https://formspree.io/f/xoeagrod", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
      let json: { ok?: boolean; success?: boolean; error?: string } = {};
      try {
        json = await res.json();
      } catch {
        // Non-JSON response — fall through to status check
      }
      if (!res.ok || json.ok === false || json.success === false) {
        setContactError(json.error || "Something went wrong. Please try again.");
        return;
      }
      setContactDone(true);
    } catch {
      setContactError("Network error. Please try again.");
    } finally {
      setContactSubmitting(false);
    }
  }

  function submitWaitlist(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setWaitlistError(null);
    setWaitlistSending(true);

    const callbackName = `jsonp_callback_${Date.now()}`;
    const params = new URLSearchParams();
    params.set("EMAIL", waitlistEmail);
    params.set("tags", "981");
    params.set(HONEYPOT_NAME, "");
    params.set("c", callbackName);

    const script = document.createElement("script");
    script.src = `${MC_JSONP_BASE}&${params.toString()}`;

    const cleanup = () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
      script.remove();
      setWaitlistSending(false);
    };

    (window as unknown as Record<string, unknown>)[callbackName] = (
      response: { result?: string; msg?: string } | undefined,
    ) => {
      cleanup();
      if (response?.result === "success") {
        setWaitlistDone(true);
        return;
      }
      const raw = (response?.msg ?? "").replace(/<[^>]*>/g, "").trim();
      const safe =
        raw && raw.length <= 140 ? raw.replace(/^\d+\s*-\s*/, "") : "";
      setWaitlistError(safe || "Something went wrong — please try again.");
    };

    script.onerror = () => {
      cleanup();
      setWaitlistError("Something went wrong — please try again.");
    };

    document.body.appendChild(script);
  }

  return (

    <div style={{ background: CREAM, color: INK }} className="min-h-screen">
      {/* Dioro product bar (secondary nav, sits under the main SaaSed header) */}
      <div
        className="sticky top-0 z-30 w-full border-b-2"
        style={{ background: CREAM, borderColor: INK }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="#top" aria-label="Dioro home">
            <img
              src={dioroLogoAsset.url}
              alt="Dioro by SaaSed"
              width="140"
              height="47"
              className="h-auto w-[140px]"
            />
          </a>

          <nav className="hidden items-center gap-8 md:flex" aria-label="Dioro sections">
            {[
              { href: "#use-cases", label: "Use cases" },
              { href: "#how-it-works", label: "How it works" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm font-medium tracking-wide hover:opacity-70"
                style={{ fontFamily: BODY }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <a
            href="#cta"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold"
            style={{
              background: LIME,
              color: INK,
              border: `2px solid ${INK}`,
              fontFamily: BODY,
            }}
          >
            Access Dioro
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* Hero — diagonal cream-to-lime wash */}
      <section id="top" className="hero-wash w-full">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 py-16 md:px-12 md:py-24 lg:grid-cols-2 lg:gap-16">
          {/* Left — headline */}
          <div className="flex flex-col gap-6">
            <span
              className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em]"
              style={{ background: "transparent", color: INK, border: `2px solid ${INK}`, fontFamily: BODY }}
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Salesforce license intelligence
            </span>

            <h1
              className="mt-6 text-5xl font-extrabold leading-[0.95] tracking-[-0.02em] text-[#1a1408] md:text-7xl"
              style={{ fontFamily: "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif" }}
            >
              See clearly
              <br />
              through
              <br />
              your Salesforce
              <br />
              <span
                className="px-1"
                style={{
                  background: `linear-gradient(to top, ${LIME} 0%, ${LIME} 32%, transparent 32%)`,
                }}
              >
                licenses.
              </span>
            </h1>

            <p
              className="mt-6 max-w-md text-lg font-normal leading-[1.625] text-[#6b5f42]"
              style={{ fontFamily: "'DM Sans', ui-sans-serif, system-ui, sans-serif", color: MUTED }}
            >
              Dioro audits how every seat, permission set and feature is really
              used — then turns it into forecasts, savings and benchmarks you can
              take into your next renewal.
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a
                href="#cta"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold"
                style={{ background: INK, color: CREAM, fontFamily: BODY }}
              >
                Request access
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#use-cases"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold"
                style={{
                  background: "transparent",
                  color: INK,
                  border: `2px solid ${INK}`,
                  fontFamily: BODY,
                }}
              >
                Explore use cases
              </a>
            </div>

            <ul className="flex flex-wrap gap-x-6 gap-y-2 pt-2" style={{ fontFamily: BODY }}>
              {["Read-only access", "No agents installed", "Results in days"].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm" style={{ color: MUTED }}>
                  <Check className="h-4 w-4" style={{ color: INK }} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — license health card */}
          <div className="relative flex items-center justify-center lg:justify-end">

            <div
              className="w-full max-w-md rounded-2xl p-6"
              style={{ ...CARD_BORDER, ...CARD_SHADOW, background: CREAM }}
            >
              <div className="mb-5 flex items-center justify-between">
                <span className="text-lg font-bold" style={{ fontFamily: DISPLAY }}>
                  License health
                </span>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold"
                  style={{ background: LIME, border: `2px solid ${INK}`, fontFamily: BODY }}
                >
                  Live
                </span>
              </div>

              <div className="flex flex-col gap-4">
                {[
                  { label: "Sales Cloud — Enterprise", used: 68 },
                  { label: "Service Cloud", used: 91 },
                  { label: "Platform Starter", used: 34 },
                  { label: "CPQ", used: 52 },
                ].map((row) => (
                  <div key={row.label} className="flex flex-col gap-1.5">
                    <div
                      className="flex items-center justify-between text-sm"
                      style={{ fontFamily: BODY }}
                    >
                      <span>{row.label}</span>
                      <span>{row.used}% active</span>
                    </div>
                    <div
                      className="h-2.5 w-full overflow-hidden rounded-full"
                      style={{ background: SAND, border: `1.5px solid ${INK}` }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${row.used}%`, background: LIME }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="mt-5 rounded-xl px-4 py-3 text-sm font-bold"
                style={{
                  background: SAND,
                  color: INK,
                  border: `2px solid ${INK}`,
                  fontFamily: BODY,
                }}
              >
                248 dormant seats detected · est. £412k annual saving
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof strip — dark */}
      <section className="w-full" style={{ background: INK }}>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 py-12 md:grid-cols-3 md:px-12 md:py-14">
          {proofPoints.map((p) => (
            <div key={p.value} className="flex flex-col gap-2">
              <span
                className="text-4xl font-bold md:text-5xl"
                style={{ color: LIME, fontFamily: DISPLAY }}
              >
                {p.value}
              </span>
              <span
                className="text-sm leading-snug md:text-base"
                style={{ color: SAND, fontFamily: BODY }}
              >
                {p.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases */}
      <section id="use-cases" className="w-full">
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-12 md:py-24">
          <div className="mb-12 flex flex-col gap-4">
            <p
              className="text-xs font-bold uppercase tracking-[0.22em]"
              style={{ color: MUTED, fontFamily: BODY }}
            >
              Four ways teams use Dioro
            </p>
            <h2
              className="max-w-xl text-3xl font-bold leading-tight md:text-4xl lg:text-5xl"
              style={{ fontFamily: DISPLAY }}
            >
              From raw license data to decisions
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              return (
                <div
                  key={uc.kicker}
                  className="flex flex-col gap-4 rounded-2xl p-8"
                  style={{ ...CARD_BORDER, ...CARD_SHADOW, background: CREAM }}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="flex h-12 w-12 items-center justify-center rounded-full"
                      style={{ background: LIME, border: `2px solid ${INK}` }}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span
                      className="text-2xl font-bold"
                      style={{ color: "#c9c9b4", fontFamily: DISPLAY }}
                    >
                      {uc.kicker}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold md:text-2xl" style={{ fontFamily: DISPLAY }}>
                    {uc.title}
                  </h3>
                  <p
                    className="text-base leading-relaxed"
                    style={{ color: MUTED, fontFamily: BODY }}
                  >
                    {uc.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's next */}
      <section id="future" className="w-full" style={{ background: SAND }}>
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-12 md:py-24">
          <div className="flex flex-col gap-4">
            <span
              className="inline-flex w-fit items-center gap-2 text-xs font-bold uppercase tracking-[0.18em]"
              style={{ background: "transparent", color: LIME, fontFamily: BODY }}
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Coming soon
            </span>
            <h2
              className="text-3xl font-bold leading-tight md:text-4xl lg:text-5xl"
              style={{ color: INK, fontFamily: DISPLAY }}
            >

What's next for Dioro
            </h2>
          </div>

          <div className="mt-10 flex flex-col gap-8">
            <div className="flex items-start gap-4">
              <span
                className="text-lg font-bold leading-none"
                style={{ color: LIME, fontFamily: BODY }}
              >
                •
              </span>
              <div className="flex flex-col gap-1">
                <h3 className="text-lg font-bold" style={{ color: INK, fontFamily: DISPLAY }}>
                  Usage forecasting
                </h3>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: MUTED, fontFamily: BODY }}
                >
                  Project seat demand across the renewal horizon using real adoption curves, hiring plans and seasonality.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span
                className="text-lg font-bold leading-none"
                style={{ color: LIME, fontFamily: BODY }}
              >
                •
              </span>
              <div className="flex flex-col gap-1">
                <h3 className="text-lg font-bold" style={{ color: INK, fontFamily: DISPLAY }}>
                  Benchmarking
                </h3>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: MUTED, fontFamily: BODY }}
                >
                  Compare your license mix, cost per active user and adoption against peers of similar size and industry.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex w-full justify-center">
            <button
              type="button"
              onClick={() => setWaitlistOpen(true)}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold"
              style={{
                background: LIME,
                color: INK,
                border: `2px solid ${INK}`,
                fontFamily: BODY,
              }}
            >
              Join the Waitlist
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="w-full" style={{ background: "rgba(157,200,0,0.08)" }}>
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-12 md:py-24">
          <div className="mb-12 flex flex-col gap-3">
            <h2 className="text-3xl font-bold leading-tight md:text-4xl lg:text-5xl" style={{ fontFamily: DISPLAY }}>
              Three steps, no disruption
            </h2>
            <p className="text-base md:text-lg" style={{ color: MUTED, fontFamily: BODY }}>
              Read-only from the first minute to the last.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                step: "Connect",
                body: "Connect, read only, easy to install, easy to uninstall, nothing changed",
              },
              {
                step: "Audit",
                body: "Dioro maps licenses, permission sets, logins and feature usage into one clear model.",
              },
              {
                step: "Act",
                body: "Get a prioritised action plan and enter your renewal conversation with intelligence",
              },
            ].map((s, i) => (
              <div
                key={s.step}
                className="flex flex-col gap-4 rounded-2xl p-8"
                style={{ ...CARD_BORDER, ...CARD_SHADOW, background: "#ffffff" }}
              >
                <span
                  className="text-sm font-bold uppercase tracking-[0.18em]"
                  style={{ color: MUTED, fontFamily: BODY }}
                >
                  Step {i + 1}
                </span>
                <h3 className="text-2xl font-bold" style={{ fontFamily: DISPLAY }}>
                  {s.step}
                </h3>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: MUTED, fontFamily: BODY }}
                >
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Dioro / Origin */}
      <section id="why-dioro" className="w-full">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center md:px-12 md:py-24">
          <img
            src={dioroSeesawAsset.url}
            alt=""
            width="56"
            height="48"
            className="mx-auto mb-6 h-12 w-14 object-contain"
          />
          <blockquote
            className="text-2xl font-semibold leading-snug md:text-3xl lg:text-4xl"
            style={{ fontFamily: DISPLAY, color: INK }}
          >
            "Dioro originates from the Greek word{" "}
            <span
              style={{
                backgroundColor: LIME,
                padding: "0 6px",
                color: INK,
              }}
            >
              dioratikotita (insights)
            </span>{" "}
            - the ability to see clearly through a complex situation."
          </blockquote>
          <p
            className="mt-6 text-sm font-bold uppercase tracking-[0.18em] whitespace-pre-wrap"
            style={{ color: MUTED, fontFamily: BODY }}
          >
            {"\n\n"}A SAASED COMPANY
          </p>
        </div>
      </section>

      {/* Divider above CTA */}
      <div className="w-full" style={{ borderTop: `2px solid ${INK}` }} />

      {/* CTA */}
      <section id="cta" className="w-full" style={{ background: HERO_WASH_GREEN }}>
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-12 md:py-24">
          <div className="grid grid-cols-1 items-center gap-12 md:gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="flex flex-col gap-3">
              <h2
                className="text-3xl font-bold leading-tight md:text-4xl lg:text-5xl"
                style={{ color: INK, fontFamily: DISPLAY }}
              >
                Find out what your licenses are really costing you.
              </h2>
              <p
                className="max-w-xl text-base md:text-lg"
                style={{ color: MUTED, fontFamily: BODY }}
              >
                Tell us who you are. We follow up personally — no automated
                onboarding.
              </p>
            </div>

            <div
              className="w-full rounded-2xl p-6 md:p-8"
              style={{ ...CARD_BORDER, ...CARD_SHADOW, background: "#ffffff" }}
            >
              {contactDone ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-full"
                    style={{ background: LIME, color: INK }}
                  >
                    <Check className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <p
                    className="text-base font-medium"
                    style={{ color: INK, fontFamily: BODY }}
                  >
                    We will get back to you within a day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submitContact} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="dioro-name"
                      className="text-sm font-semibold"
                      style={{ color: INK, fontFamily: BODY }}
                    >
                      Name
                    </label>
                    <input
                      id="dioro-name"
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      className="w-full rounded-xl px-4 py-3 text-base outline-none transition focus:ring-2"
                      style={{
                        border: `2px solid ${INK}`,
                        background: CREAM,
                        color: INK,
                        fontFamily: BODY,
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="dioro-company"
                      className="text-sm font-semibold"
                      style={{ color: INK, fontFamily: BODY }}
                    >
                      Company Name
                    </label>
                    <input
                      id="dioro-company"
                      name="company"
                      type="text"
                      required
                      autoComplete="organization"
                      className="w-full rounded-xl px-4 py-3 text-base outline-none transition focus:ring-2"
                      style={{
                        border: `2px solid ${INK}`,
                        background: CREAM,
                        color: INK,
                        fontFamily: BODY,
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="dioro-email"
                      className="text-sm font-semibold"
                      style={{ color: INK, fontFamily: BODY }}
                    >
                      Email
                    </label>
                    <input
                      id="dioro-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      onChange={() => {
                        if (emailError) setEmailError(null);
                      }}
                      className="w-full rounded-xl px-4 py-3 text-base outline-none transition focus:ring-2"
                      style={{
                        border: `2px solid ${INK}`,
                        background: CREAM,
                        color: INK,
                        fontFamily: BODY,
                      }}
                    />
                    {emailError && (
                      <p
                        className="text-sm font-medium"
                        style={{ color: "#b00020", fontFamily: BODY }}
                      >
                        {emailError}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="dioro-phone"
                      className="text-sm font-semibold"
                      style={{ color: INK, fontFamily: BODY }}
                    >
                      Phone
                    </label>
                    <input
                      id="dioro-phone"
                      name="phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      className="w-full rounded-xl px-4 py-3 text-base outline-none transition focus:ring-2"
                      style={{
                        border: `2px solid ${INK}`,
                        background: CREAM,
                        color: INK,
                        fontFamily: BODY,
                      }}
                    />
                  </div>

                  {contactError && (
                    <p
                      className="text-sm font-medium"
                      style={{ color: "#b00020", fontFamily: BODY }}
                    >
                      {contactError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold disabled:opacity-70"
                    style={{ ...CARD_BORDER, background: LIME, color: INK, fontFamily: BODY }}
                  >
                    {contactSubmitting && (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    )}
                    Get Dioro
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Divider + footer bar */}
      <div className="w-full" style={{ borderTop: `2px solid ${INK}` }} />
      <div className="w-full" style={{ background: INK }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-2 px-6 py-6 md:flex-row md:justify-between">
          <p className="text-sm" style={{ color: SAND, fontFamily: BODY }}>
            © 2026 Dioro — part of SaaSed.co
          </p>
          <p className="text-sm" style={{ color: SAND, fontFamily: BODY }}>
            Salesforce license audit & optimisation
          </p>
        </div>
      </div>

      {waitlistOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: "rgba(26,20,8,0.6)" }}
          onClick={() => setWaitlistOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Join the Dioro waitlist"
        >
          <div
            className="relative w-full max-w-md rounded-2xl p-8"
            style={{ ...CARD_BORDER, ...CARD_SHADOW, background: CREAM }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setWaitlistOpen(false)}
              aria-label="Close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full"
              style={{ border: `2px solid ${INK}`, background: CREAM }}
            >
              <X className="h-4 w-4" style={{ color: INK }} aria-hidden="true" />
            </button>

            <h2
              className="text-2xl font-bold"
              style={{ fontFamily: DISPLAY, color: INK }}
            >
              Get notified when it's ready
            </h2>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ fontFamily: BODY, color: MUTED }}
            >
              Usage forecasting and benchmarking are coming to Dioro. Leave
              your email and we'll let you know the moment they're live.
            </p>

            {waitlistDone ? (
              <div className="mt-6 flex flex-col gap-2">
                <p
                  className="text-xl font-bold"
                  style={{ fontFamily: DISPLAY, color: INK }}
                >
                  You're on the list
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{ fontFamily: BODY, color: MUTED }}
                >
                  Thanks — we'll let you know the moment they're launch.
                </p>
              </div>
            ) : (
              <form className="mt-6 flex flex-col gap-4" onSubmit={submitWaitlist}>
                <input
                  type="email"
                  name="EMAIL"
                  required
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="you@company.com"
                  aria-label="Email address"
                  className="w-full rounded-full px-5 py-3 text-sm"
                  style={{
                    background: "#ffffff",
                    border: `2px solid ${INK}`,
                    fontFamily: BODY,
                    color: INK,
                  }}
                />
                <input
                  type="text"
                  name={HONEYPOT_NAME}
                  tabIndex={-1}
                  defaultValue=""
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-5000px" }}
                />
                {waitlistError && (
                  <p
                    className="text-xs"
                    style={{ fontFamily: BODY, color: "#b3341a" }}
                  >
                    {waitlistError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={waitlistSending}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold disabled:opacity-70"
                  style={{
                    background: LIME,
                    color: INK,
                    border: `2px solid ${INK}`,
                    fontFamily: BODY,
                  }}
                >
                  Notify me
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </form>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
